import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Typography, Button, TextField, Chip, Stack, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, Tooltip,
  LinearProgress,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { getPipelineRuns, getPipelineDashboard, getProjectPipelineAnalytics, triggerAdoPipeline, triggerGitHubWorkflow } from '../../api/client'
import type { TriggerAdoRequest, TriggerGitHubRequest, AdoBuildRun, PipelineHealth, PipelineStats } from '../../api/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildStatusColor = (run: AdoBuildRun): 'success' | 'warning' | 'error' | 'default' => {
  if (run.status === 'inProgress' || run.status === 'notStarted') return 'warning'
  if (run.status === 'completed') {
    if (run.result === 'succeeded') return 'success'
    if (run.result === 'failed')    return 'error'
    if (run.result === 'canceled')  return 'default'
  }
  return 'default'
}

const buildLabel = (run: AdoBuildRun) =>
  run.status === 'completed' ? (run.result ?? run.status) : run.status

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' =>
  s === 'inProgress' || s === 'queued' ? 'warning'
    : s === 'succeeded' || s === 'completed' ? 'success'
    : s === 'failed' ? 'error'
    : 'default'

function relativeTime(iso?: string) {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 2)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function branchShort(ref: string) {
  return ref.replace(/^refs\/heads\//, '')
}

// ── Health helpers ────────────────────────────────────────────────────────────

const HEALTH_COLOR: Record<PipelineHealth, string> = {
  Healthy: '#10b981',
  Flaky:   '#f59e0b',
  Failing: '#ef4444',
}

const HEALTH_BG: Record<PipelineHealth, string> = {
  Healthy: '#f0fdf4',
  Flaky:   '#fffbeb',
  Failing: '#fef2f2',
}

function HealthIcon({ h, size = 15 }: { h: PipelineHealth, size?: number }) {
  const sx = { fontSize: size, color: HEALTH_COLOR[h] }
  if (h === 'Healthy') return <CheckCircleOutlineIcon sx={sx} />
  if (h === 'Flaky')   return <WarningAmberIcon sx={sx} />
  return <ErrorOutlineIcon sx={sx} />
}

function cardHealth(runs: AdoBuildRun[]): PipelineHealth {
  const completed = runs.filter(r => r.status === 'completed')
  if (completed.length === 0) return 'Healthy'
  const failures = completed.filter(r => r.result === 'failed').length
  const rate = failures / completed.length
  if (rate >= 0.5) return 'Failing'
  if (rate >= 0.15) return 'Flaky'
  return 'Healthy'
}

// ── Analytics panel ───────────────────────────────────────────────────────────

const TOP_STEPS = [300, 1000, 5000]

function AnalyticsPanel({ project }: { project: string }) {
  const [top, setTop] = useState(TOP_STEPS[0])

  const { data: res, isLoading, isFetching } = useQuery({
    queryKey: ['pipeline-analytics', project, top],
    queryFn: () => getProjectPipelineAnalytics(project, top),
    staleTime: 5 * 60_000,
  })

  if (isLoading) return (
    <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <CircularProgress size={18} />
      <Typography variant="body2" color="text.secondary">Loading pipeline analytics…</Typography>
    </Paper>
  )

  if (!res?.success || !res.data) return (
    <Alert severity="error">Failed to load analytics for {project}</Alert>
  )

  const { pipelines, recentFailures, hasAccess, accessError } = res.data

  if (!hasAccess) return (
    <Alert severity="warning" icon={<LockOutlinedIcon />}>{accessError}</Alert>
  )

  const counts = {
    Failing: pipelines.filter(p => p.health === 'Failing').length,
    Flaky:   pipelines.filter(p => p.health === 'Flaky').length,
    Healthy: pipelines.filter(p => p.health === 'Healthy').length,
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700}>{project} · Pipeline Health</Typography>
        <Stack direction="row" gap={1}>
          {counts.Failing > 0 && <Chip size="small" label={`${counts.Failing} failing`}  sx={{ bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 600 }} />}
          {counts.Flaky   > 0 && <Chip size="small" label={`${counts.Flaky} flaky`}     sx={{ bgcolor: '#fffbeb', color: '#d97706', fontWeight: 600 }} />}
          {counts.Healthy > 0 && <Chip size="small" label={`${counts.Healthy} healthy`} sx={{ bgcolor: '#f0fdf4', color: '#10b981', fontWeight: 600 }} />}
        </Stack>
      </Box>

      {pipelines.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No completed builds found.</Typography>
      ) : (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
          <Box component="thead">
            <Box component="tr">
              {['', 'Pipeline', 'Fail rate', 'Streak', 'Last run', 'Last failure', ''].map((h, i) => (
                <Box key={i} component="th" sx={{
                  textAlign: 'left', pb: 1, pr: 2, borderBottom: '1px solid #e2e8f0',
                  fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>{h}</Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {pipelines.map((p, i) => (
              <PipelineRow key={i} p={p} />
            ))}
          </Box>
        </Box>
      )}

      {pipelines.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
            {pipelines.length} pipelines · {top.toLocaleString()} builds analysed
          </Typography>
          {TOP_STEPS.indexOf(top) < TOP_STEPS.length - 1 && (
            <Button size="small" variant="text" disabled={isFetching}
              sx={{ fontSize: 11, py: 0 }}
              onClick={() => setTop(TOP_STEPS[TOP_STEPS.indexOf(top) + 1])}>
              {isFetching ? 'Loading…' : `Load more (↑${TOP_STEPS[TOP_STEPS.indexOf(top) + 1].toLocaleString()} builds)`}
            </Button>
          )}
        </Box>
      )}

      {recentFailures.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
            Recent Failures
          </Typography>
          <Stack gap={0.5}>
            {recentFailures.map(run => (
              <Box key={run.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                py: 0.75, borderBottom: '1px solid #f1f5f9', '&:last-child': { border: 'none' }
              }}>
                <ErrorOutlineIcon sx={{ fontSize: 14, color: '#ef4444', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                  {run.pipelineName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, fontSize: 11 }}>
                  {branchShort(run.sourceBranch)}
                </Typography>
                <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: 11, minWidth: 64, textAlign: 'right' }}>
                  {relativeTime(run.finishTime ?? run.queueTime)}
                </Typography>
                {run.url && (
                  <a href={run.url} target="_blank" rel="noreferrer" style={{ color: '#94a3b8', display: 'flex', flexShrink: 0 }}>
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                  </a>
                )}
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  )
}

function PipelineRow({ p }: { p: PipelineStats }) {
  return (
    <Box component="tr" sx={{ '&:hover td': { bgcolor: '#f8fafc' } }}>
      <Box component="td" sx={{ py: 1, pr: 1.5, borderBottom: '1px solid #f1f5f9' }}>
        <HealthIcon h={p.health} />
      </Box>
      <Box component="td" sx={{ py: 1, pr: 2, borderBottom: '1px solid #f1f5f9', maxWidth: 220 }}>
        <Tooltip title={p.name}>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
            {p.name}
          </Typography>
        </Tooltip>
      </Box>
      <Box component="td" sx={{ py: 1, pr: 2, borderBottom: '1px solid #f1f5f9', minWidth: 120 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate" value={p.failRate * 100}
            sx={{ flex: 1, height: 6, borderRadius: 3,
              bgcolor: '#e2e8f0',
              '& .MuiLinearProgress-bar': { bgcolor: HEALTH_COLOR[p.health], borderRadius: 3 }
            }}
          />
          <Typography variant="caption" sx={{ fontSize: 11, minWidth: 32, color: HEALTH_COLOR[p.health], fontWeight: 600 }}>
            {Math.round(p.failRate * 100)}%
          </Typography>
        </Box>
      </Box>
      <Box component="td" sx={{ py: 1, pr: 2, borderBottom: '1px solid #f1f5f9' }}>
        {p.consecutiveFailures > 0 ? (
          <Chip size="small" label={`${p.consecutiveFailures}×`}
            sx={{ fontSize: 10, height: 18, bgcolor: HEALTH_BG[p.health], color: HEALTH_COLOR[p.health], fontWeight: 700 }} />
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </Box>
      <Box component="td" sx={{ py: 1, pr: 2, borderBottom: '1px solid #f1f5f9' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {relativeTime(p.lastRun?.toString())}
        </Typography>
      </Box>
      <Box component="td" sx={{ py: 1, pr: 2, borderBottom: '1px solid #f1f5f9' }}>
        <Typography variant="caption" sx={{ fontSize: 11, color: p.lastFailure ? '#ef4444' : 'text.disabled' }}>
          {p.lastFailure ? relativeTime(p.lastFailure.toString()) : '—'}
        </Typography>
      </Box>
      <Box component="td" sx={{ py: 1, borderBottom: '1px solid #f1f5f9' }}>
        {p.lastRunUrl && (
          <a href={p.lastRunUrl} target="_blank" rel="noreferrer" style={{ color: '#94a3b8', display: 'flex' }}>
            <OpenInNewIcon sx={{ fontSize: 13 }} />
          </a>
        )}
      </Box>
    </Box>
  )
}

// ── Pipeline Dashboard ────────────────────────────────────────────────────────

function PipelineDashboard() {
  const [selected, setSelected] = useState<string | null>(null)

  const { data: res, isLoading } = useQuery({
    queryKey: ['pipeline-dashboard'],
    queryFn: getPipelineDashboard,
    refetchInterval: 60_000,
  })

  if (isLoading) return <CircularProgress size={20} />
  if (!res?.success || !res.data || res.data.projects.length === 0) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
        ADO Projects
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
        {res.data.projects.map(proj => {
          const isSelected = selected === proj.project
          const health = proj.hasAccess ? cardHealth(proj.recentRuns) : null
          const active  = proj.recentRuns.filter(r => r.status === 'inProgress').length

          return (
            <Paper
              key={proj.project}
              variant="outlined"
              onClick={() => proj.hasAccess && setSelected(isSelected ? null : proj.project)}
              sx={{
                p: 2, cursor: proj.hasAccess ? 'pointer' : 'default',
                borderColor: isSelected ? '#3b82f6' : '#e2e8f0',
                borderWidth: isSelected ? 2 : 1,
                transition: 'border-color 0.15s',
                '&:hover': proj.hasAccess ? { borderColor: isSelected ? '#3b82f6' : '#94a3b8' } : {},
              }}
            >
              {/* Card header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: proj.hasAccess ? 1.5 : 0 }}>
                {!proj.hasAccess
                  ? <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  : health && <HealthIcon h={health} size={14} />}
                <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>{proj.project}</Typography>
                {active > 0 && (
                  <Chip size="small" label={`${active} running`}
                    sx={{ fontSize: 10, height: 18, bgcolor: '#eff6ff', color: '#3b82f6', fontWeight: 600 }} />
                )}
                {health && health !== 'Healthy' && (
                  <Typography variant="caption" sx={{ fontSize: 10, color: HEALTH_COLOR[health], fontWeight: 600 }}>
                    {proj.recentRuns.filter(r => r.status === 'completed' && r.result === 'failed').length} failures
                  </Typography>
                )}
              </Box>

              {!proj.hasAccess ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: 12 }}>
                  {proj.accessError}
                </Typography>
              ) : proj.recentRuns.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>No recent builds.</Typography>
              ) : (
                <Stack gap={0.5}>
                  {proj.recentRuns.map(run => (
                    <Box key={run.id} sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      py: 0.4, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' }
                    }}>
                      <Chip label={buildLabel(run)} color={buildStatusColor(run)} size="small"
                        sx={{ fontSize: 10, height: 18, minWidth: 68 }} />
                      <Tooltip title={run.pipelineName}>
                        <Typography variant="body2" sx={{
                          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12,
                        }}>
                          {run.pipelineName}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: 11 }}>
                        {relativeTime(run.finishTime ?? run.queueTime)}
                      </Typography>
                      {run.url && (
                        <a href={run.url} target="_blank" rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ display: 'flex', color: '#94a3b8', flexShrink: 0 }}>
                          <OpenInNewIcon sx={{ fontSize: 13 }} />
                        </a>
                      )}
                    </Box>
                  ))}
                </Stack>
              )}

              {proj.hasAccess && (
                <Typography variant="caption" color={isSelected ? 'primary' : 'text.disabled'}
                  sx={{ mt: 1.5, display: 'block', fontSize: 11 }}>
                  {isSelected ? '▲ Hide analytics' : '▼ View analytics'}
                </Typography>
              )}
            </Paper>
          )
        })}
      </Box>

      {selected && <AnalyticsPanel project={selected} />}
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PipelinesPage() {
  const [adoOpen, setAdoOpen] = useState(false)
  const [ghOpen, setGhOpen] = useState(false)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['pipeline-runs'],
    queryFn: () => getPipelineRuns(50),
    refetchInterval: 10_000,
  })

  const adoMutation = useMutation({
    mutationFn: triggerAdoPipeline,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pipeline-runs'] }); setAdoOpen(false) },
  })
  const ghMutation = useMutation({
    mutationFn: triggerGitHubWorkflow,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pipeline-runs'] }); setGhOpen(false) },
  })

  const runs = data?.data ?? []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" fontWeight={600}>Pipelines</Typography>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" size="small" onClick={() => setAdoOpen(true)}>Trigger ADO</Button>
          <Button variant="outlined" size="small" onClick={() => setGhOpen(true)}>Trigger GitHub</Button>
        </Stack>
      </Stack>

      <PipelineDashboard />

      <Box>
        <Typography variant="subtitle2" fontWeight={700} mb={1.5} color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
          Triggered Runs
        </Typography>

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Pipeline</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Triggered</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No runs yet</TableCell></TableRow>
              ) : runs.map(r => (
                <TableRow key={r.id}>
                  <TableCell><Chip label={r.source.toUpperCase()} size="small" /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.pipeline}</TableCell>
                  <TableCell>{r.branch ?? '—'}</TableCell>
                  <TableCell><Chip label={r.status} color={statusColor(r.status)} size="small" /></TableCell>
                  <TableCell>{new Date(r.triggeredAt).toLocaleString()}</TableCell>
                  <TableCell>
                    {r.runUrl && (
                      <Button size="small" href={r.runUrl} target="_blank" endIcon={<OpenInNewIcon fontSize="small" />}>
                        View
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      </Box>

      <TriggerAdoDialog
        open={adoOpen}
        onClose={() => setAdoOpen(false)}
        onSubmit={r => adoMutation.mutate(r)}
        loading={adoMutation.isPending}
        error={adoMutation.error?.message}
      />
      <TriggerGitHubDialog
        open={ghOpen}
        onClose={() => setGhOpen(false)}
        onSubmit={r => ghMutation.mutate(r)}
        loading={ghMutation.isPending}
        error={ghMutation.error?.message}
      />
    </Box>
  )
}

function TriggerAdoDialog({ open, onClose, onSubmit, loading, error }: {
  open: boolean; onClose: () => void
  onSubmit: (r: TriggerAdoRequest) => void
  loading: boolean; error?: string
}) {
  const [project, setProject] = useState('')
  const [pipelineId, setPipelineId] = useState('')
  const [branch, setBranch] = useState('main')

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Trigger ADO Pipeline</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Project" value={project} onChange={e => setProject(e.target.value)} fullWidth required />
          <TextField label="Pipeline ID" value={pipelineId} onChange={e => setPipelineId(e.target.value)} fullWidth required />
          <TextField label="Branch" value={branch} onChange={e => setBranch(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading || !project || !pipelineId}
          onClick={() => onSubmit({ project, pipelineId, branch })}>
          {loading ? <CircularProgress size={18} /> : 'Trigger'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function TriggerGitHubDialog({ open, onClose, onSubmit, loading, error }: {
  open: boolean; onClose: () => void
  onSubmit: (r: TriggerGitHubRequest) => void
  loading: boolean; error?: string
}) {
  const [repo, setRepo] = useState('')
  const [workflow, setWorkflow] = useState('')
  const [branch, setBranch] = useState('main')

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dispatch GitHub Workflow</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Repository" value={repo} onChange={e => setRepo(e.target.value)} fullWidth required />
          <TextField label="Workflow file (e.g. deploy.yml)" value={workflow} onChange={e => setWorkflow(e.target.value)} fullWidth required />
          <TextField label="Branch" value={branch} onChange={e => setBranch(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading || !repo || !workflow}
          onClick={() => onSubmit({ repo, workflow, branch })}>
          {loading ? <CircularProgress size={18} /> : 'Dispatch'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
